import React, { useState, useEffect } from "react";
import { getBackendBaseUrl } from "../../utils/apiUrl";

export default function ApiKeysSettings() {
  const [tavusApiKey, setTavusApiKey] = useState("");
  const [openaiApiKey, setOpenaiApiKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [showTavus, setShowTavus] = useState(false);
  const [showOpenai, setShowOpenai] = useState(false);

  const baseUrl = getBackendBaseUrl() + "/chatbot";

  useEffect(() => {
    const fetchKeys = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;
        const res = await fetch(`${baseUrl}/user/keys`, {
          headers: {
            "Authorization": `Bearer ${token}`,
          },
        });
        if (res.ok) {
          const json = await res.json();
          if (json.success && json.data) {
            setTavusApiKey(json.data.tavusApiKey || "");
            setOpenaiApiKey(json.data.openaiApiKey || "");
          }
        }
      } catch (err) {
        console.error("Failed to load user API keys:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchKeys();
  }, [baseUrl]);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setStatusMsg("");
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token is missing. Please log in again.");
      }
      const res = await fetch(`${baseUrl}/user/keys`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ tavusApiKey, openaiApiKey }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setStatusMsg("API credentials updated successfully!");
      } else {
        throw new Error(json.message || "Failed to update API keys.");
      }
    } catch (err) {
      console.error(err);
      setStatusMsg("Error: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ padding: "40px", maxWidth: "700px", margin: "0 auto", fontFamily: "'Outfit', 'Inter', sans-serif" }}>
      <div style={{ background: "#ffffff", border: "1px solid #E2E8F0", borderRadius: "20px", padding: "40px", boxShadow: "0 10px 30px rgba(0,0,0,0.04)" }}>
        <div style={{ marginBottom: "32px" }}>
          <h2 style={{ fontSize: "1.75rem", fontWeight: "700", color: "#0F172A", margin: "0 0 8px 0" }}>🔑 Integration API Keys</h2>
          <p style={{ fontSize: "0.9rem", color: "#64748B", margin: 0 }}>Configure the credentials used across all your AI assistants and avatars.</p>
        </div>

        {isLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#64748B" }}>
            <span style={{ fontSize: "1.1rem" }}>Loading saved API keys...</span>
          </div>
        ) : (
          <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <label style={{ fontSize: "0.9rem", fontWeight: "600", color: "#334155" }}>Avatar API Key (Tavus)</label>
                <button
                  type="button"
                  onClick={() => setShowTavus(!showTavus)}
                  style={{ background: "none", border: "none", color: "#6366F1", fontSize: "0.8rem", cursor: "pointer", fontWeight: "500" }}
                >
                  {showTavus ? "Hide" : "Show"}
                </button>
              </div>
              <input
                type={showTavus ? "text" : "password"}
                value={tavusApiKey}
                onChange={(e) => setTavusApiKey(e.target.value)}
                placeholder="Enter your Tavus API Key"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  border: "1px solid #CBD5E1",
                  fontSize: "0.95rem",
                  fontFamily: "monospace",
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "border-color 0.2s",
                }}
              />
            </div>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <label style={{ fontSize: "0.9rem", fontWeight: "600", color: "#334155" }}>Text to Text API Key (OpenAI)</label>
                <button
                  type="button"
                  onClick={() => setShowOpenai(!showOpenai)}
                  style={{ background: "none", border: "none", color: "#6366F1", fontSize: "0.8rem", cursor: "pointer", fontWeight: "500" }}
                >
                  {showOpenai ? "Hide" : "Show"}
                </button>
              </div>
              <input
                type={showOpenai ? "text" : "password"}
                value={openaiApiKey}
                onChange={(e) => setOpenaiApiKey(e.target.value)}
                placeholder="Enter your OpenAI API Key"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  border: "1px solid #CBD5E1",
                  fontSize: "0.95rem",
                  fontFamily: "monospace",
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "border-color 0.2s",
                }}
              />
            </div>

            <button
              type="submit"
              disabled={isSaving}
              style={{
                background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
                color: "#ffffff",
                border: "none",
                padding: "14px",
                borderRadius: "12px",
                fontWeight: "600",
                fontSize: "1rem",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(99, 102, 241, 0.25)",
                transition: "all 0.2s",
                marginTop: "12px",
              }}
            >
              {isSaving ? "Saving Credentials..." : "Save API Credentials"}
            </button>

            {statusMsg && (
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: "10px",
                  fontSize: "0.85rem",
                  textAlign: "center",
                  fontWeight: "500",
                  backgroundColor: statusMsg.startsWith("Error") ? "#FEF2F2" : "#ECFDF5",
                  color: statusMsg.startsWith("Error") ? "#EF4444" : "#10B981",
                  border: `1px solid ${statusMsg.startsWith("Error") ? "#FCA5A5" : "#6EE7B7"}`,
                }}
              >
                {statusMsg}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
